import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ClosedPositionsTable from './ClosedPositionsTable';
import DividendsTable from './DividendsTable';
import WithdrawalsTable from './WithdrawalsTable';
import TaxYearSummary from './TaxYearSummary';
import './RealizedGains.css';
import { API_BASE_URL } from '../lib/api';

function RealizedGains() {
  const [allTransactions, setAllTransactions] = useState({ closedPositions: [], dividends: [], withdrawals: [] });
  const [activeSubTab, setActiveSubTab] = useState('summary'); // 'closed' | 'dividends' | 'withdrawals' | 'summary'
  const [yearlyData, setYearlyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Fetch all transactions on mount to populate counts
    fetchAllTransactions();

    if (activeSubTab === 'summary') {
      fetchAllYearsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSubTab]);

  const fetchAllTransactions = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch data for the last 10 years
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let i = 0; i < 10; i++) {
        years.push(currentYear - i);
      }

      const promises = years.map(year =>
        axios.get(`${API_BASE_URL}/transactions/tax-report/${year}`, {
          withCredentials: true
        }).then(response => response.data).catch(() => null)
      );

      const results = await Promise.all(promises);

      // Combine all closed positions, dividends, and withdrawals
      const allClosedPositions = [];
      const allDividends = [];
      const allWithdrawals = [];

      results.forEach(result => {
        if (result) {
          if (result.closedPositions) {
            allClosedPositions.push(...result.closedPositions);
          }
          if (result.dividends) {
            allDividends.push(...result.dividends);
          }
          if (result.withdrawals) {
            allWithdrawals.push(...result.withdrawals);
          }
        }
      });

      // Sort by date descending (most recent first)
      allClosedPositions.sort((a, b) => new Date(b.date) - new Date(a.date));
      allDividends.sort((a, b) => new Date(b.date) - new Date(a.date));
      allWithdrawals.sort((a, b) => new Date(b.date) - new Date(a.date));

      setAllTransactions({
        closedPositions: allClosedPositions,
        dividends: allDividends,
        withdrawals: allWithdrawals
      });
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllYearsData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch data for the last 10 years
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let i = 0; i < 10; i++) {
        years.push(currentYear - i);
      }

      const promises = years.map(year =>
        axios.get(`${API_BASE_URL}/transactions/tax-report/${year}`, {
          withCredentials: true
        }).then(response => ({
          year,
          summary: response.data.summary
        })).catch(() => null) // Ignore years with no data
      );

      const results = await Promise.all(promises);
      // Filter out nulls and years with no transactions
      const validYears = results.filter(result =>
        result && result.summary &&
        (result.summary.totalRealizedGains?.EUR !== 0 ||
         result.summary.totalDividends?.EUR !== 0 ||
         result.summary.totalWithdrawals?.EUR !== 0)
      );

      setYearlyData(validYears);
    } catch (err) {
      console.error('Error fetching yearly data:', err);
      setError('Failed to load yearly summary');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="realized-gains-container">
        <h1>Realized Gains & Dividends</h1>
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="realized-gains-container">
        <h1>Realized Gains & Dividends</h1>
        <p className="error-message">{error}</p>
        <button onClick={() => activeSubTab === 'summary' ? fetchAllYearsData() : fetchAllTransactions()} className="btn-retry">Retry</button>
      </div>
    );
  }

  return (
    <div className="realized-gains-container">
      <div className="realized-gains-header">
        <h1>Realized Gains & Dividends</h1>
      </div>

      {/* Sub-tabs */}
      <div className="sub-tabs">
        <button
          onClick={() => setActiveSubTab('summary')}
          className={activeSubTab === 'summary' ? 'active' : ''}
        >
          📊 Summary
        </button>
        <button
          onClick={() => setActiveSubTab('closed')}
          className={activeSubTab === 'closed' ? 'active' : ''}
        >
          💰 Closed Positions ({allTransactions.closedPositions.length})
        </button>
        <button
          onClick={() => setActiveSubTab('dividends')}
          className={activeSubTab === 'dividends' ? 'active' : ''}
        >
          💵 Dividends ({allTransactions.dividends.length})
        </button>
        <button
          onClick={() => setActiveSubTab('withdrawals')}
          className={activeSubTab === 'withdrawals' ? 'active' : ''}
        >
          💸 Withdrawals ({allTransactions.withdrawals.length})
        </button>
      </div>

      {/* Content */}
      <div className="realized-gains-content">
        {activeSubTab === 'summary' && (
          <TaxYearSummary yearlyData={yearlyData} />
        )}

        {activeSubTab === 'closed' && (
          <ClosedPositionsTable
            positions={allTransactions.closedPositions}
            onRefresh={fetchAllTransactions}
          />
        )}

        {activeSubTab === 'dividends' && (
          <DividendsTable
            dividends={allTransactions.dividends}
            onRefresh={fetchAllTransactions}
          />
        )}

        {activeSubTab === 'withdrawals' && (
          <WithdrawalsTable
            withdrawals={allTransactions.withdrawals}
            onRefresh={fetchAllTransactions}
          />
        )}
      </div>
    </div>
  );
}

export default RealizedGains;
